import React, { useCallback, useEffect, useState } from 'react'
import { Button, Input, Modal, Spin, Table, Tag, message } from 'antd'

import { getApiErrorMessage } from '../../utils/api'
import {
  getInstallmentPlan,
  voidInstallmentPayment
} from '../../api/installment'
import {
  INSTALLMENT_METHOD_LABEL,
  installmentMoney as money
} from './installmentShared'

/**
 * 分期明細：各期排程 + 收款紀錄（可作廢）。
 *
 * props:
 *   open, plan（至少要有 _id；列表上那份會先拿來顯示，再撈完整明細）
 *   businessId, canManage
 *   onClose()
 *   onChanged()  作廢收款之後呼叫，讓外層重抓列表
 */
const InstallmentDetailModal = ({
  open,
  plan,
  businessId,
  canManage,
  onClose,
  onChanged
}) => {
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    if (!plan?._id || !businessId) return
    setLoading(true)
    setDetail({ plan, payments: [] })
    try {
      const res = await getInstallmentPlan(businessId, plan._id)
      setDetail(res?.data?.data || { plan, payments: [] })
    } catch (err) {
      message.error(getApiErrorMessage(err, '取得分期明細失敗'))
    } finally {
      setLoading(false)
    }
  }, [plan, businessId])

  useEffect(() => {
    if (open) load()
    else setDetail(null)
  }, [open, load])

  const handleVoidPayment = (payment) => {
    let reason = ''
    Modal.confirm({
      title: '作廢這筆收款？',
      content: (
        <div>
          <p style={{ marginBottom: 8 }}>
            {payment.paidDate} · {INSTALLMENT_METHOD_LABEL[payment.method]} ·{' '}
            {money(payment.amount)}
          </p>
          <p style={{ fontSize: 12, color: '#86868b', marginBottom: 8 }}>
            作廢後這筆錢會從現金流與已收金額扣回，紀錄仍會保留。
            {payment.method === 'storedValue' && payment.storedValueDeducted &&
              ' 扣掉的儲值金會退回顧客的儲值餘額。'}
          </p>
          <Input.TextArea
            rows={2}
            placeholder='作廢原因（選填）'
            onChange={(e) => {
              reason = e.target.value
            }}
          />
        </div>
      ),
      okText: '作廢',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await voidInstallmentPayment(businessId, payment._id, reason)
          message.success('已作廢')
          await load()
          onChanged && onChanged()
        } catch (err) {
          message.error(getApiErrorMessage(err, '作廢失敗'))
        }
      }
    })
  }

  const current = detail?.plan || plan

  return (
    <Modal
      title={`分期明細 — ${current?.customerName || ''}`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={760}
    >
      <Spin spinning={loading}>
        {current?.orderCode && (
          <div style={{ fontSize: 13, color: '#86868b' }}>
            訂單 {current.orderCode} · 分期總額 {money(current.totalWithFee)} · 已收{' '}
            {money(current.paidAmount)} · 未收 {money(current.outstanding)}
          </div>
        )}
        <div style={{ fontWeight: 600, margin: '12px 0 8px' }}>各期排程</div>
        <Table
          size='small'
          rowKey='period'
          pagination={false}
          scroll={{ x: 'max-content' }}
          dataSource={current?.schedule || []}
          columns={[
            { title: '期數', dataIndex: 'period', width: 70, render: (v) => `第 ${v} 期` },
            { title: '到期日', dataIndex: 'dueDate', width: 120 },
            { title: '應收', dataIndex: 'amount', align: 'right', render: (v) => money(v) },
            { title: '已收', dataIndex: 'paidAmount', align: 'right', render: (v) => money(v) },
            {
              title: '狀態',
              dataIndex: 'status',
              width: 90,
              render: (v) =>
                v === 'paid' ? (
                  <Tag color='green'>已收</Tag>
                ) : v === 'partial' ? (
                  <Tag color='orange'>部分</Tag>
                ) : v === 'waived' ? (
                  <Tag>已減免</Tag>
                ) : (
                  <Tag color='default'>未收</Tag>
                )
            }
          ]}
        />

        <div style={{ fontWeight: 600, margin: '20px 0 8px' }}>收款紀錄</div>
        <Table
          size='small'
          rowKey='_id'
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: '還沒有收過款' }}
          dataSource={detail?.payments || []}
          columns={[
            { title: '收款日', dataIndex: 'paidDate', width: 120 },
            {
              title: '金額',
              dataIndex: 'amount',
              align: 'right',
              render: (v, row) => (
                <span
                  style={{
                    textDecoration: row.isVoided ? 'line-through' : 'none',
                    color: row.isVoided ? '#bbb' : undefined
                  }}
                >
                  {money(v)}
                </span>
              )
            },
            {
              title: '方式',
              dataIndex: 'method',
              width: 100,
              render: (v) => INSTALLMENT_METHOD_LABEL[v] || v
            },
            { title: '經手人', dataIndex: 'receivedByName', width: 100 },
            {
              title: '',
              key: 'action',
              width: 80,
              render: (_, row) =>
                row.isVoided ? (
                  <Tag>已作廢</Tag>
                ) : canManage ? (
                  <Button size='small' type='text' danger onClick={() => handleVoidPayment(row)}>
                    作廢
                  </Button>
                ) : null
            }
          ]}
        />
      </Spin>
    </Modal>
  )
}

export default InstallmentDetailModal
